/**
 * ChatSearchModal — global search across every Doserne chat room the
 * current user belongs to. Text search (default) hits message bodies;
 * "Bilder" filter returns every photo message; "Talenotater" for audio.
 *
 * Selecting a result closes the modal and calls `onOpenRoom(room_id,
 * message_id)` — the parent decides whether to open a floating window
 * (FAB) or navigate to the full page.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Search, X, Loader2, Image as ImageIcon, Mic } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KINDS = [
  { id: "all",   label: "Tekst" },
  { id: "photo", label: "Bilder" },
  { id: "audio", label: "Talenotater" },
];

function highlight(body, q) {
  if (!q) return body;
  const idx = body.toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return body;
  return (
    <>
      {body.slice(0, idx)}
      <mark className="bg-amber-200 text-slate-900 rounded px-0.5">
        {body.slice(idx, idx + q.length)}
      </mark>
      {body.slice(idx + q.length)}
    </>
  );
}

export default function ChatSearchModal({ open, onClose, authHeader, onOpenRoom }) {
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  const run = useCallback(async (query, k) => {
    if (k === "all" && !query.trim()) { setResults([]); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/doserne/chat/search`, {
        ...authHeader(),
        params: { q: query.trim(), kind: k, limit: 60 },
      });
      setResults(data.results || []);
    } catch { setResults([]); }
    setLoading(false);
  }, [authHeader]);

  // Debounced search.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => { run(q, kind); }, 250);
    return () => clearTimeout(t);
  }, [open, q, kind, run]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-slate-900/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="chat-search-modal"
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[92vh] flex flex-col shadow-2xl mt-14 sm:mt-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b flex items-center gap-2">
          <Search className="w-4 h-4 text-slate-400" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Søk i alle samtaler…"
            className="flex-1 outline-none text-sm placeholder-slate-400"
            data-testid="chat-search-input"
          />
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="chat-search-close">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="px-4 py-2 border-b border-slate-100 flex items-center gap-1">
          {KINDS.map((k) => (
            <button
              key={k.id}
              onClick={() => setKind(k.id)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-black uppercase tracking-wider ${
                kind === k.id ? "bg-[#0084ff] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              style={kind === k.id ? { color: "#fff" } : {}}
              data-testid={`chat-search-kind-${k.id}`}
            >
              {k.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : results.length === 0 ? (
            <div className="p-8 text-center text-xs text-slate-400 italic">
              {kind === "all" && !q.trim()
                ? "Skriv et søkeord for å finne meldinger."
                : "Ingen treff."}
            </div>
          ) : (
            results.map((r) => (
              <button
                key={r.id}
                onClick={() => { onOpenRoom?.(r.room_id, r.id); onClose(); }}
                className="w-full text-left px-4 py-2.5 border-b border-slate-100 hover:bg-slate-50 flex items-start gap-3"
                data-testid={`chat-search-result-${r.id}`}
              >
                {r.photo_url ? (
                  <img src={r.photo_url} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                ) : r.audio_url ? (
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                    <Mic className="w-4 h-4" />
                  </div>
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-slate-200 text-slate-700 text-xs font-black flex items-center justify-center flex-shrink-0">
                    {(r.sender_name || "?").slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="text-xs font-black text-slate-900 truncate">{r.room_label || "Samtale"}</div>
                    <div className="text-[10px] text-slate-400 truncate">· {r.sender_name}</div>
                  </div>
                  <div className="text-xs text-slate-600 truncate">
                    {r.body ? highlight(r.body, q) : r.photo_url ? (
                      <span className="inline-flex items-center gap-1 text-slate-500"><ImageIcon className="w-3 h-3" /> Bilde</span>
                    ) : r.audio_url ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700"><Mic className="w-3 h-3" /> Talenotat</span>
                    ) : ""}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">{new Date(r.created_at).toLocaleString("no-NO")}</div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
